import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowDownTrayIcon, CircleStackIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ButtonLinkList from '@ui/button/ButtonLinkList';
import FormButton from '@ui/button/FormButton';
import ToolbarButton from '@ui/button/ToolbarButton';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import ChartCard, { ChartCardState } from '@ui/chart/ChartCard';
import DataTable, { DataTableColumn, DataTableRow, DataTableRowLimit } from '@ui/chart/DataTable';
import DataTableMatrix from '@ui/chart/DataTableMatrix';
import FormInputNumber from '@ui/form/input/FormInputNumber';
import FormSelect from '@ui/form/input/FormSelect';
import Message from '@ui/message/Message';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import { PickupAlertBasis, PickupAlertDirection, PickupAlertSetting, PickupBookings, PickupProperty, PropertyGroup } from '@app/model/bi/Bi';
import {
	adrOf, alertsOf, basisOf, BASES, BookingRow, bookingRows, bookTimeOf, CHANGE_LABEL, Kinds, matrixBlocks, matrixGroups, MatrixBasis, MatrixScope,
	MONTH_SHORT, Revenue, stayYearsOf, thresholdOk,
} from './PickupMonitoring.utilities';

// The cards of BI Pick-up monitoring (ROO2-314 Part 2). The summary and the stay-month table take the answer of
// findPickupMonitoring; the ADR alerts and the bookings list (step 2b) the answer of findPickupBookings, which loads apart.
// Each card takes the revenue of the switches.

const r2 = (v: number | null) => v === null ? null : Math.round(v * 100) / 100;
const negative = (v: number | null) => v === null ? null : -Math.abs(v);

// --- Pick-up summary (D15, D20, D32): one row with a column group per kind --------------------------------------------------
const KIND_INFO = 'New bookings: made in the period and on the books at its end (a booking made and cancelled in the period is left out). '
	+ 'Cancellations: on the books before the period, cancelled in it. Changes to older bookings: longer or shorter stays and price changes of '
	+ 'bookings made before the period. Net pick-up = new − cancellations + changes.';

export const SummaryCard: React.FunctionComponent<{ kinds: Kinds | null, label: string, period: string, phone: boolean, state: ChartCardState,
	caption: string, onRetry: () => void }> = ({ kinds, label, period, phone, state, caption, onRetry }) => {
	const k = kinds;
	const kindList = k === null ? [] : [
		{ id: 'fresh', label: 'New bookings', p: k.fresh, adr: true, minus: false },
		{ id: 'canc', label: 'Cancellations', p: k.canc, adr: true, minus: true },
		{ id: 'changes', label: 'Changes to older bookings', p: k.changes, adr: false, minus: false },
	].filter(x => x.p.bookings > 0);
	const columns: DataTableColumn[] = [{ id: 'what', label: '', sortable: false }];
	const groups = [{ label: '', span: 1 }];
	const cells: Record<string, string | number | null> = { what: label };
	if (k !== null && !phone) {
		kindList.forEach(x => {
			columns.push({ id: `${x.id}B`, label: 'Bookings', format: 'number', sortable: false },
				{ id: `${x.id}Rn`, label: 'RN', format: 'number', signed: true, tone: 'change', sortable: false },
				{ id: `${x.id}Rev`, label: 'Revenue', format: 'money', signed: true, tone: 'change', sortable: false });
			cells[`${x.id}B`] = x.p.bookings;
			cells[`${x.id}Rn`] = x.minus ? -Math.abs(x.p.roomNights) : x.p.roomNights;
			cells[`${x.id}Rev`] = r2(x.minus ? negative(x.p.revenue) : x.p.revenue);
			if (x.adr) {
				columns.push({ id: `${x.id}Adr`, label: 'ADR', format: 'money', decimals: 2, sortable: false });
				cells[`${x.id}Adr`] = adrOf(x.p);
			}
			groups.push({ label: x.label, span: x.adr ? 4 : 3 });
		});
		columns.push({ id: 'netRn', label: 'RN', format: 'number', signed: true, tone: 'change', sortable: false },
			{ id: 'netRev', label: 'Revenue', format: 'money', signed: true, tone: 'change', sortable: false });
		groups.push({ label: 'Net pick-up', span: 2 });
		cells.netRn = k.net.roomNights;
		cells.netRev = r2(k.net.revenue);
	}
	// A phone keeps the room nights of each kind and the ADR of the new bookings, so that the table fits 368 px.
	const phoneColumns: DataTableColumn[] = [{ id: 'what', label: '', sortable: false },
		{ id: 'newRn', label: 'New', format: 'number', signed: true, tone: 'change', sortable: false },
		{ id: 'newAdr', label: 'ADR', format: 'money', sortable: false },
		{ id: 'cancRn', label: 'Canc.', format: 'number', signed: true, tone: 'change', sortable: false },
		{ id: 'netRn', label: 'Net', format: 'number', signed: true, tone: 'change', sortable: false }];
	return <ChartCard title="Pick-up summary" period={period} state={state} onRetry={onRetry}
		emptyLabel="No booking was received, changed or cancelled in this period."
		info={{ title: 'Pick-up summary', content: KIND_INFO }} caption={caption}>
		{k !== null && (phone
			? <DataTable ariaLabel="Pick-up by kind" density="compact" columns={phoneColumns} rows={[{ id: 'total', cells: {
				what: label === 'Group total' ? 'Group' : label === 'All properties' ? 'All' : 'Total', newRn: k.fresh.roomNights, newAdr: adrOf(k.fresh),
				cancRn: -Math.abs(k.canc.roomNights), netRn: k.net.roomNights } }]} />
			: <DataTable ariaLabel="Pick-up by kind" columns={columns} columnGroups={groups} rows={[{ id: 'total', cells }]} />)}
	</ChartCard>;
};

// --- Pick-up by stay month (D34–D43, DataTableMatrix of design system v1.31.1) ----------------------------------------------
type StayMonthProps = {
	properties: PickupProperty[],
	names: { companyHotelId: number, name: string }[],
	scope: MatrixScope,
	groups: PropertyGroup[],
	revenue: Revenue,
	revenueName: string,
	period: string,
	asOf: string,
	phone: boolean,
	state: ChartCardState,
	exporting: boolean,
	onRetry: () => void,
	onExport: (year: string, basis: MatrixBasis) => void,
};

export const StayMonthCard: React.FunctionComponent<StayMonthProps> = ({ properties, names, scope, groups, revenue, revenueName, period, asOf, phone, state,
	exporting, onRetry, onExport }) => {
	const years = useMemo(() => stayYearsOf(properties, asOf), [properties, asOf]);
	const [year, setYear] = useState(asOf.slice(0, 4));
	const [basis, setBasis] = useState<MatrixBasis>('NEW');
	useEffect(() => { if (!years.includes(year)) setYear(years[0]); }, [years, year]);
	const blockGroups = useMemo(() => matrixGroups(names, scope, groups), [names, scope, groups]);
	const blocks = useMemo(() => matrixBlocks(properties, blockGroups, year, basis, revenue), [properties, blockGroups, year, basis, revenue]);
	const months = MONTH_SHORT.map((m, i) => ({ id: `${year}-${String(i + 1).padStart(2, '0')}`, label: m }));
	// On a phone the months before the first month with figures hide; the Total keeps the year.
	const firstWith = blocks[0] === undefined ? -1 : months.findIndex((_, i) => blocks[0].values.roomNights.cells[i] !== 0 || (blocks[0].values.revenue.cells[i] ?? 1) !== 0);
	const first = phone ? Math.max(0, firstWith) : 0;
	const shown = months.slice(first);
	const shownBlocks = first === 0 ? blocks : blocks.map(b => ({ ...b, values: Object.fromEntries(Object.entries(b.values).map(([k, v]) => [k, { ...v, cells: v.cells.slice(first) }])) }));
	const measures = basis === 'NEW' ? [
		{ id: 'revenue', label: 'Revenue', shortLabel: 'Rev.', format: 'money' as const },
		{ id: 'roomNights', label: 'Room nights', shortLabel: 'RN', format: 'number' as const },
		{ id: 'adr', label: 'ADR', format: 'money' as const },
	] : [
		{ id: 'revenue', label: 'Revenue', shortLabel: 'Rev.', format: 'money' as const, signed: true, tone: 'change' as const },
		{ id: 'roomNights', label: 'Room nights', shortLabel: 'RN', format: 'number' as const, signed: true, tone: 'change' as const },
	];
	const title = scope === 'property' ? 'Pick-up by stay month' : 'Pick-up by property and stay month';
	return <ChartCard title={title} period={`${period === '' ? '' : `${period} · `}stays in ${year}`} state={state} onRetry={onRetry}
		info={{ title, content: basis === 'NEW'
			? 'Revenue and Room nights: the new bookings of the period (made in it and on the books at its end), by stay month. ADR = revenue ÷ room nights.'
			: 'Net pick-up: all nights on the books at the end of the period minus those on the books before it (new bookings − cancellations ± changes), by stay month. No ADR on the net.' }}
		actions={<ToolbarButton icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={state !== 'default' || exporting} onClick={() => onExport(year, basis)}>
			{exporting ? 'Preparing…' : 'Export'}
		</ToolbarButton>}
		caption={basis === 'NEW' ? `Revenue and Room nights = the new bookings; ADR = revenue ÷ room nights. ${revenueName}. Export gives an xlsx in this layout.`
			: `Net pick-up = new bookings − cancellations ± changes of older bookings. ${revenueName}. No ADR on the net. Export gives an xlsx in this layout.`}>
		<div className="flex flex-col gap-3">
			<div className="grid grid-cols-2 gap-x-2 gap-y-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6">
				<CaptionGroup caption="Stay year"><div className="min-w-0"><ButtonTabs label="Stay year of the columns" value={year} onChange={setYear}
					options={years.map(y => ({ value: y, label: y }))} /></div></CaptionGroup>
				<CaptionGroup caption="Show" className="col-span-2 sm:col-span-1"><div className="min-w-0"><ButtonTabs label="Figures of the table" value={basis}
					onChange={v => setBasis(v as MatrixBasis)} options={[{ value: 'NEW', label: 'New bookings' }, { value: 'NET', label: 'Net pick-up' }]} /></div></CaptionGroup>
			</div>
			<DataTableMatrix ariaLabel={`Pick-up by ${scope === 'property' ? '' : 'property, '}measure and stay month of ${year}`} frozenLabels={['Property', 'Measure']}
				columns={shown} measures={measures} blocks={shownBlocks} compact={phone} stickyHeader />
		</div>
	</ChartCard>;
};

// --- ADR alerts (D5–D12) and the bookings list (D2, D3, D13, D17, D18), canvas Version 10 --------------------------------------
const LIMIT = (count: number, word: string, top: string): DataTableRowLimit => ({
	count, moreLabel: hidden => `+ ${hidden} more ${word}`, showAllLabel: all => `Show all ${all} ${word}`, showTopLabel: top,
});
const TOO_MANY = <Message state="info" title="More than 5,000 bookings" flush>
	Pick a shorter period or a smaller scope to see the bookings. The summary and the stay-month table above count every booking.</Message>;

// The tone of the deviation chip: red below, amber above the threshold in the direction of the setting; gray otherwise.
const toneOf = (row: BookingRow, setting: PickupAlertSetting): 'critical' | 'warning' | 'neutral' | undefined => {
	const d = row.deviation;
	if (d === null) return undefined;
	if (setting.direction === 'BELOW' && d > 0) return 'neutral';
	if (setting.direction === 'ABOVE' && d < 0) return 'neutral';
	return d < -setting.threshold / 100 ? 'critical' : d > setting.threshold / 100 ? 'warning' : 'neutral';
};

const rowOf = (b: BookingRow, setting: PickupAlertSetting, nameOf: (id: number) => string, revenue: Revenue, phone: boolean): DataTableRow => {
	const tone = toneOf(b, setting);
	const arrival = b.arrival === null ? null : phone ? `${b.arrival.slice(8, 10)} ${MONTH_SHORT[Number(b.arrival.slice(5, 7)) - 1]}` : b.arrival;
	return {
		id: `${b.companyHotelId}|${b.id}`,
		cells: {
			code: b.reservationNumber, ota: b.otaNumber ?? '–', property: nameOf(b.companyHotelId), channel: b.channel.trim() === '' ? 'No channel' : b.channel,
			booked: bookTimeOf(b.bookedAt), change: CHANGE_LABEL[b.change], arrival, nights: b.nights, rooms: b.rooms,
			type: b.roomTypes.length === 0 ? '–' : b.roomTypes.join(', '), rn: b.roomNights, rnNow: b.roomNightsNow,
			rev: r2(revenue(b.revenue)), adr: r2(b.adr), ref: r2(b.reference),
			dev: b.deviation === null ? null : (Math.round(b.deviation * 100) === 0 ? 0 : Math.round(b.deviation * 100) / 100),
		},
		cellTones: tone === undefined ? undefined : { dev: tone },
		muted: b.change === 'CANCELLED',
	};
};

const columnsOf = (setting: PickupAlertSetting, many: boolean, phone: boolean): DataTableColumn[] => {
	const b = basisOf(setting.basis);
	const dev: DataTableColumn = { id: 'dev', label: phone ? 'vs Ref.' : b.vs, format: 'percent', decimals: 0, signed: true, tone: 'chip',
		info: { title: b.vs, content: `The booking ADR ÷ the ${b.short} − 1. ${b.short} = ${b.info}` } };
	if (phone) return [{ id: 'code', label: 'Booking' }, { id: 'arrival', label: 'Arrival' }, { id: 'rn', label: 'Δ RN', format: 'number', signed: true, tone: 'change' },
		{ id: 'adr', label: 'ADR', format: 'money' }, dev];
	return [
		{ id: 'code', label: 'Reservation', info: { title: 'Reservation', content: 'The reservation number (a 5stelle code without its booking time). The OTA confirmation number is its own column.' } },
		{ id: 'ota', label: 'OTA number' },
		...(many ? [{ id: 'property', label: 'Property' }] : []),
		{ id: 'channel', label: 'Channel name' }, { id: 'booked', label: 'Booked' }, { id: 'change', label: 'Change' },
		{ id: 'arrival', label: 'Arrival', format: 'date' },
		...(many ? [] : [{ id: 'nights', label: 'Nights', format: 'number' as const }]),
		{ id: 'rooms', label: 'Rooms', format: 'number' },
		{ id: 'rn', label: 'Δ RN', format: 'number', signed: true, tone: 'change' },
		{ id: 'rev', label: 'Δ REV', format: 'money', signed: true, tone: 'change' },
		{ id: 'adr', label: 'ADR', format: 'money', decimals: 2 },
		{ id: 'ref', label: b.short, format: 'money', decimals: 2 },
		dev,
	];
};

const DIRECTIONS: { value: PickupAlertDirection, label: string }[] = [{ value: 'BELOW', label: 'Below' }, { value: 'ABOVE', label: 'Above' },
	{ value: 'BOTH', label: 'Below or above' }];

type AlertsProps = {
	answer: PickupBookings | undefined,
	setting: PickupAlertSetting,
	onSave: (setting: PickupAlertSetting) => void,
	saving: boolean,
	revenue: Revenue,
	many: boolean,
	nameOf: (id: number) => string,
	period: string,
	phone: boolean,
	state: ChartCardState,
	onRetry: () => void,
};

// The controls apply at once on the screen; Save stores them in the account (D9), for every property.
export const AlertsCard: React.FunctionComponent<AlertsProps> = ({ answer, setting, onSave, saving, revenue, many, nameOf, period, phone, state, onRetry }) => {
	const form = useForm<{ basis: PickupAlertBasis, threshold: number }>({ defaultValues: { basis: setting.basis, threshold: setting.threshold } });
	const [direction, setDirection] = useState<PickupAlertDirection>(setting.direction);
	useEffect(() => { form.reset({ basis: setting.basis, threshold: setting.threshold }); setDirection(setting.direction); }, [setting, form]);
	const basis = form.watch('basis');
	const threshold = Number(form.watch('threshold'));
	const live: PickupAlertSetting = { basis, direction, threshold: thresholdOk(threshold) ? threshold : setting.threshold };
	const rows = useMemo(() => answer === undefined || answer.tooMany ? [] : bookingRows(answer, live.basis, revenue), [answer, live.basis, revenue]);
	const checked = rows.filter(r => r.deviation !== null && r.change !== 'CANCELLED').length;
	const alerts = alertsOf(rows, live);
	const columns = columnsOf(live, many, phone).filter(c => !['change', 'rev', 'booked', 'ota', ...(many ? ['nights', 'rooms', 'channel'] : [])].includes(c.id))
		.flatMap(c => c.id === 'rn' ? [...(phone ? [] : [{ id: 'type', label: 'Room type' }]), { ...c, id: 'rnNow', label: 'RN', signed: false, tone: undefined }] : [c]);
	const options = BASES.map(b => ({ id: b.value, value: b.value, label: b.label, extraInfo: `${b.flagged} flagged` }));
	const changed = live.basis !== setting.basis || live.direction !== setting.direction || live.threshold !== setting.threshold;
	const ready = state === 'default' || state === 'refreshing';
	return <ChartCard title={`ADR alerts${ready && answer !== undefined && !answer.tooMany ? ` · ${alerts.length}` : ''}`} period={period}
		state={state === 'empty' ? 'default' : state} onRetry={onRetry}
		info={{ title: 'ADR alerts', content: 'The bookings of the period that are on the books (new, longer, shorter or with a price change) whose ADR is more than the set percent from the reference ADR. A cancelled booking has no alert. The ADR and the references are on the books now.' }}
		caption={`${checked} ${checked === 1 ? 'booking' : 'bookings'} checked. The basis, the direction and the percent are saved in your account and apply to every property. Sorted by the deviation, largest first.`}>
		<div className="flex flex-col gap-3">
			<div className="grid grid-cols-2 gap-x-2 gap-y-4 pt-2 sm:flex sm:flex-wrap sm:items-end sm:gap-x-6">
				<div className="relative z-20 col-span-2 w-full sm:w-96"><FormSelect form={form} name="basis" label="Compare with" options={options} size="w-full" optionsSize="xxlarge" /></div>
				<CaptionGroup caption="Show" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
					<div className="w-full min-w-0"><ButtonTabs label="Direction of the deviation" value={direction} onChange={v => setDirection(v as PickupAlertDirection)}
						options={phone ? DIRECTIONS.map(d => d.value === 'BOTH' ? { ...d, label: 'Both' } : d) : DIRECTIONS} /></div>
				</CaptionGroup>
				<div className="col-span-2 flex items-end gap-2 sm:col-span-1">
					<div className="w-24"><FormInputNumber form={form} name="threshold" label="More than (%)" min={1} max={100} step={1} /></div>
					<FormButton state="white" onClick={() => onSave(live)} disabled={!changed || saving || !thresholdOk(threshold)}>{saving ? 'Saving' : 'Save'}</FormButton>
				</div>
			</div>
			{ready && answer !== undefined && (answer.tooMany ? TOO_MANY : alerts.length === 0
				? <Message state="success" title="No alert" flush>No booking of the period is more than {live.threshold} % from the {basisOf(live.basis).short}.</Message>
				: <DataTable ariaLabel="ADR alerts" density={phone || many ? 'compact' : 'comfortable'} columns={columns}
					rows={alerts.map(b => rowOf(b, live, nameOf, revenue, phone))} rowLimit={LIMIT(15, 'alerts', 'Show the first 15')} />)}
		</div>
	</ChartCard>;
};

type BookingsProps = {
	answer: PickupBookings | undefined,
	setting: PickupAlertSetting,
	revenue: Revenue,
	many: boolean,
	nameOf: (id: number) => string,
	scopeName: string,
	period: string,
	phone: boolean,
	state: ChartCardState,
	exporting: boolean,
	onRetry: () => void,
	onExport: (file: 'CURRENT_VIEW' | 'ALL_DATA') => void,
};

export const BookingsCard: React.FunctionComponent<BookingsProps> = ({ answer, setting, revenue, many, nameOf, scopeName, period, phone, state, exporting,
	onRetry, onExport }) => {
	const rows = useMemo(() => answer === undefined || answer.tooMany ? [] : bookingRows(answer, setting.basis, revenue), [answer, setting.basis, revenue]);
	const b = basisOf(setting.basis);
	const rn = rows.reduce((t, x) => t + x.roomNights, 0);
	const rev = rows.reduce<number | null>((t, x) => { const v = revenue(x.revenue); return t === null || v === null ? null : t + v; }, 0);
	const tooMany = answer?.tooMany === true;
	const empty = answer !== undefined && !tooMany && rows.length === 0;
	return <ChartCard title="Bookings" period={period} state={empty && state === 'default' ? 'empty' : state} onRetry={onRetry} emptyLabel="No booking in this period."
		actions={<ButtonLinkList toolbar icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={state !== 'default' || tooMany || empty || exporting}
			description={`Excel · ${scopeName} · ${period}`}
			items={[
				{ name: 'Current view', detail: 'The bookings as they show now, with the deviation of your Compare with', icon: <TableCellsIcon className="h-4 w-4" />,
					onClick: () => onExport('CURRENT_VIEW') },
				{ name: 'All data', detail: 'Each stay night of the bookings, before and after the period', icon: <CircleStackIcon className="h-4 w-4" />,
					onClick: () => onExport('ALL_DATA') },
			]}>
			{exporting ? 'Preparing…' : 'Export'}
		</ButtonLinkList>}
		caption={`One row per booking (a booking with more rooms is one row; Rooms gives the count). Newest first. Booked = the booking time (hotel time). Δ RN and Δ REV = the change in the period: the whole booking for a new booking, minus for a cancellation. ${b.vs}: see the ⓘ.`}>
		{tooMany ? TOO_MANY : rows.length > 0 && <DataTable ariaLabel="Bookings of the period" density={phone || many ? 'compact' : 'comfortable'}
			columns={columnsOf(setting, many, phone).filter(c => c.id !== 'ref')} rows={rows.map(x => rowOf(x, setting, nameOf, revenue, phone))}
			rowLimit={LIMIT(25, 'bookings', 'Show the newest 25')} footer={{ id: 'total', cells: { code: `Total (${rows.length})`, rn, rev: r2(rev) } }} />}
	</ChartCard>;
};
